/**
 * Design tokens del Sistema ATS.
 *
 * Fuente única de verdad para color, tipografía, espaciado, radios y sombras.
 * Ningún componente debería declarar un color hexadecimal propio: si hace falta
 * un color nuevo, se añade aquí primero.
 *
 * Paleta: 1 primario (azul) + 1 acento (teal) + escala de grises neutros.
 * Todos los pares texto/fondo cumplen contraste WCAG AA (>= 4.5:1).
 */

// ---------------------------------------------------------------------------
// Color
// ---------------------------------------------------------------------------

/** Grises neutros. Base de superficies, bordes y texto. */
export const neutral = {
  0: '#ffffff',
  50: '#f8fafc',
  100: '#f1f5f9',
  200: '#e2e8f0',
  300: '#cbd5e1',
  400: '#94a3b8',
  500: '#64748b',
  600: '#475569',
  700: '#334155',
  800: '#1e293b',
  900: '#0f172a',
}

/** Azul primario. `main` sobre blanco = 6.3:1 (AA para texto normal). */
export const brand = {
  50: '#eff6ff',
  100: '#dbeafe',
  200: '#bfdbfe',
  300: '#93c5fd',
  400: '#60a5fa',
  500: '#3b82f6',
  600: '#2563eb',
  700: '#1d4ed8',
  800: '#1e40af',
  900: '#1e3a8a',
}

/** Teal de acento. `main` sobre blanco = 5.5:1 (AA para texto normal). */
export const accent = {
  50: '#f0fdfa',
  100: '#ccfbf1',
  200: '#99f6e4',
  300: '#5eead4',
  400: '#2dd4bf',
  500: '#14b8a6',
  600: '#0d9488',
  700: '#0f766e',
  800: '#115e59',
  900: '#134e4a',
}

/** Colores semánticos. Reservados para estado, nunca para decoración. */
export const semantic = {
  success: { light: '#22c55e', main: '#15803d', dark: '#166534', surface: '#f0fdf4' },
  warning: { light: '#f59e0b', main: '#b45309', dark: '#92400e', surface: '#fffbeb' },
  error: { light: '#ef4444', main: '#b91c1c', dark: '#991b1b', surface: '#fef2f2' },
  info: { light: '#0ea5e9', main: '#0369a1', dark: '#075985', surface: '#f0f9ff' },
}

/**
 * Colores de datos para gráficos y KPIs.
 * Serie limitada y ordenada: usar siempre en este orden, no inventar tonos.
 */
export const dataViz = {
  compras: brand[600],
  ventas: accent[600],
  neutro: neutral[400],
  serie: [brand[600], accent[600], '#7c3aed', '#db2777', '#ea580c', '#0891b2'],
}

// ---------------------------------------------------------------------------
// Espaciado — escala de 8px (con medio paso de 4px vía spacing(0.5))
// ---------------------------------------------------------------------------

/** Unidad base de MUI: theme.spacing(n) => n * 8px. */
export const spacingUnit = 8

/** Espaciados semánticos, en unidades de `spacingUnit`. */
export const space = {
  /** Separación interna mínima entre elementos hermanos: 4px */
  xxs: 0.5,
  /** 8px */
  xs: 1,
  /** 16px */
  sm: 2,
  /** 24px */
  md: 3,
  /** 32px */
  lg: 4,
  /** 48px */
  xl: 6,
  /** 64px */
  xxl: 8,
}

/** Padding de página, mobile-first. */
export const pagePadding = { xs: 2, sm: 3, md: 4 }

/** Padding interno de tarjetas/paneles, mobile-first. */
export const surfacePadding = { xs: 2, sm: 3 }

// ---------------------------------------------------------------------------
// Tipografía — 2 familias como máximo: UI (sans) y datos (mono)
// ---------------------------------------------------------------------------

export const fontFamily = {
  sans: [
    '"Inter"',
    '-apple-system',
    'BlinkMacSystemFont',
    '"Segoe UI"',
    'Roboto',
    '"Helvetica Neue"',
    'Arial',
    'sans-serif',
  ].join(','),
  mono: [
    '"SFMono-Regular"',
    'ui-monospace',
    'Menlo',
    'Consolas',
    '"Liberation Mono"',
    'monospace',
  ].join(','),
}

export const fontWeight = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
}

/**
 * Tamaño mínimo de fuente en campos de formulario.
 * 16px evita el zoom automático de iOS Safari al enfocar un input.
 */
export const inputFontSize = '1rem'

// ---------------------------------------------------------------------------
// Forma y elevación
// ---------------------------------------------------------------------------

export const radius = {
  sm: 8,
  md: 12,
  lg: 16,
  pill: 999,
}

/** Sombras suaves y de bajo contraste; la jerarquía la da el borde, no la sombra. */
export const shadow = {
  xs: '0 1px 2px 0 rgba(15, 23, 42, 0.05)',
  sm: '0 1px 3px 0 rgba(15, 23, 42, 0.08), 0 1px 2px -1px rgba(15, 23, 42, 0.06)',
  md: '0 4px 12px -2px rgba(15, 23, 42, 0.08), 0 2px 6px -2px rgba(15, 23, 42, 0.05)',
  lg: '0 12px 28px -8px rgba(15, 23, 42, 0.14), 0 4px 10px -4px rgba(15, 23, 42, 0.06)',
}

// ---------------------------------------------------------------------------
// Ergonomía táctil / layout
// ---------------------------------------------------------------------------

/** Área táctil mínima recomendada (WCAG 2.5.5 / HIG). */
export const touchTarget = 44

/** Alto de la barra de navegación inferior en móvil. */
export const bottomNavHeight = 60

/** Ancho del drawer lateral en escritorio. */
export const drawerWidth = 264

export default {
  neutral,
  brand,
  accent,
  semantic,
  dataViz,
  spacingUnit,
  space,
  pagePadding,
  surfacePadding,
  fontFamily,
  fontWeight,
  inputFontSize,
  radius,
  shadow,
  touchTarget,
  bottomNavHeight,
  drawerWidth,
}
